import { useEffect, useRef } from "react";

interface DonutChartProps {
  value: number; // 0-1
  color: string;
  target?: number | null;
  subtitle?: string;
  size?: number;
}

export function DonutChart({ value, color, target, subtitle, size = 120 }: DonutChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pct = Math.round(value * 100);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 16;
    const lineWidth = 20;

    // Track
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = lineWidth;
    ctx.stroke();

    // Value arc
    if (value > 0) {
      ctx.beginPath();
      ctx.arc(cx, cy, radius, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * value);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = "round";
      ctx.stroke();
    }

    if (target !== null && target !== undefined) {
      const angle = -Math.PI / 2 + Math.PI * 2 * target;
      const inner = radius - lineWidth / 2 - 4;
      const outer = radius + lineWidth / 2 + 4;
      const targetPct = Math.round(target * 100);
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(angle) * inner, cy + Math.sin(angle) * inner);
      ctx.lineTo(cx + Math.cos(angle) * outer, cy + Math.sin(angle) * outer);
      ctx.strokeStyle = pct >= targetPct ? "#166534" : "#7f1d1d";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.stroke();
    }

    // Center text
    ctx.fillStyle = color;
    ctx.font = `bold ${Math.round(size * 0.23)}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${pct}%`, cx, subtitle ? cy - 6 : cy);

    if (subtitle) {
      ctx.fillStyle = "var(--text2)";
      ctx.font = `${Math.round(size * 0.1)}px system-ui, sans-serif`;
      ctx.fillText(subtitle, cx, cy + 14);
    }
  }, [value, color, target, subtitle, size, pct]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: size, height: size }}
      data-testid="donut-chart"
    />
  );
}
